import { type FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isSuperAdmin } from '@foundry/shared';
import {
  useCreateInvitation,
  useInvitations,
  useMe,
  useRegenerateInvitationLink,
  useRevokeInvitation,
} from '../../api/auth.js';
import { ApiError } from '../../api/client.js';
import { Alert, Button, Card, Field, Spinner } from '../../components/ui.js';

const roleLabel = (role: string): string => (role === 'developer' ? 'developer' : 'end user');

export function PeoplePage() {
  const { data: user } = useMe();
  const invitations = useInvitations();
  const create = useCreateInvitation();
  const revoke = useRevokeInvitation();
  const regenerate = useRegenerateInvitationLink();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'founder' | 'developer'>('founder');
  const [link, setLink] = useState<string | null>(null);
  const [retrieved, setRetrieved] = useState<{ id: string; url: string } | null>(null);

  const linkFor = (token: string): string =>
    `${window.location.origin}/accept-invite?token=${token}`;

  // Inviting is a super admin power; a developer who lands here is bounced.
  if (user && !isSuperAdmin(user.role)) return <Navigate to="/console" replace />;

  const onInvite = (event: FormEvent): void => {
    event.preventDefault();
    if (!email.trim()) return;
    create.mutate(
      { email: email.trim(), role },
      {
        onSuccess: (res) => {
          setLink(linkFor(res.token));
          setEmail('');
        },
      },
    );
  };

  const inUse = create.error instanceof ApiError && create.error.code === 'email_in_use';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">Invitations</h1>
        <p className="opacity-70">
          Invite end users and developers. There is no self-registration — everyone joins by
          invitation.
        </p>
      </div>

      <Card>
        <h2 className="mb-3 font-title text-xl tracking-wide">Invite someone</h2>
        <form className="flex flex-wrap items-end gap-3" onSubmit={onInvite}>
          <div className="min-w-64 flex-1">
            <Field
              label="Email"
              type="email"
              placeholder="person@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Role</span>
            <select
              className="rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={role}
              onChange={(e) => setRole(e.target.value as 'founder' | 'developer')}
            >
              <option value="founder">End user</option>
              <option value="developer">Developer</option>
            </select>
          </label>
          <Button type="submit" disabled={create.isPending || !email.trim()}>
            {create.isPending ? 'Inviting…' : 'Send invite'}
          </Button>
        </form>
        {inUse ? (
          <div className="mt-3">
            <Alert>An account with that email already exists.</Alert>
          </div>
        ) : create.isError ? (
          <div className="mt-3">
            <Alert>Could not create the invitation. Please try again.</Alert>
          </div>
        ) : null}
        {link ? (
          <div className="mt-4 rounded-md bg-neutral/60 p-3">
            <p className="mb-1 text-sm font-medium">Invitation link — send this to them:</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded border border-secondary/20 bg-white px-2 py-1 text-xs"
              />
              <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(link)}>
                Copy
              </Button>
            </div>
            <p className="mt-2 text-xs opacity-60">
              Email delivery isn’t wired up yet, so share this link directly. It expires in 7 days.
            </p>
          </div>
        ) : null}
      </Card>

      <Card>
        <h2 className="mb-3 font-title text-xl tracking-wide">Pending invitations</h2>
        {invitations.isPending ? (
          <Spinner />
        ) : invitations.isError ? (
          <Alert>Could not load invitations.</Alert>
        ) : invitations.data.length === 0 ? (
          <p className="opacity-70">No pending invitations.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {invitations.data.map((inv) => (
              <li key={inv.id} className="flex flex-col gap-2 rounded-md bg-neutral/50 p-3">
                <div className="flex items-center gap-3">
                  <span className="flex-1 truncate text-sm">{inv.email}</span>
                  <span className="text-xs uppercase tracking-wide opacity-60">
                    {roleLabel(inv.role)}
                  </span>
                  <Button
                    variant="secondary"
                    disabled={regenerate.isPending}
                    onClick={() =>
                      regenerate.mutate(inv.id, {
                        onSuccess: (token) => setRetrieved({ id: inv.id, url: linkFor(token) }),
                      })
                    }
                  >
                    Get link
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(inv.id)}
                  >
                    Revoke
                  </Button>
                </div>
                {retrieved?.id === inv.id ? (
                  <div className="rounded-md bg-white p-2">
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={retrieved.url}
                        onFocus={(e) => e.currentTarget.select()}
                        className="flex-1 rounded border border-secondary/20 bg-white px-2 py-1 text-xs"
                      />
                      <Button
                        variant="secondary"
                        onClick={() => void navigator.clipboard?.writeText(retrieved.url)}
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="mt-1 text-xs opacity-60">
                      A fresh link — any earlier link for this invite no longer works. Expires in 7
                      days.
                    </p>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
